import React, { memo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Icon,
  ListSubheader,
  Menu,
  MenuItem,
  Stack,
  Typography
} from 'investira.react.components';

/**
 * Exibe um menu controlado com opções de ordenação.
 *
 * @param {Object} pProps Propriedades do componente.
 * @param {string} pProps.id Prefixo estável dos IDs do botão e do menu.
 * @param {string} pProps.value Valor atualmente selecionado.
 * @param {{label: string, value: string}[]} pProps.options Opções disponíveis.
 * @param {Object[]} pProps.groups Agrupamento visual opcional das opções.
 * @param {Function} pProps.onChange Recebe o valor da opção selecionada.
 * @param {string} pProps.ariaLabel Rótulo acessível do botão.
 * @returns {JSX.Element} Controle de ordenação.
 */
const SortButton = memo(pProps => {
  const { id, value, options, groups = null, onChange, ariaLabel = 'Ordenar resultados' } = pProps;
  const [anchorEl, setAnchorEl] = useState(null);
  const xSelectedOption = options.find(pOption => pOption.value === value) || options[0];
  const xSelectedLabel = xSelectedOption?.selectedLabel || xSelectedOption?.label || '';
  const xButtonId = `${id}-button`;

  /** Publica a opção escolhida e fecha o menu. */
  function pvSelectOption(pOption) {
    onChange(pOption.value);
    setAnchorEl(null);
  }

  /** Renderiza as opções de um nível respeitando sua indentação visual. */
  function pvRenderOptions(pOptions, pLevel = 0) {
    return pOptions.map(pOption => (
      <MenuItem
        key={`${pOption.value}-${pOption.label}`}
        selected={value === pOption.value}
        sx={{ pl: 2 + pLevel * 2 }}
        onClick={() => pvSelectOption(pOption)}>
        {pOption.label}
      </MenuItem>
    ));
  }

  /** Renderiza grupos e subgrupos sem alterar a lista usada como contrato. */
  function pvRenderGroups(pGroups, pLevel = 0) {
    return pGroups.flatMap(pGroup => [
      <ListSubheader
        key={`group-${pLevel}-${pGroup.id}`}
        disableSticky
        sx={{ fontWeight: pLevel === 0 ? 700 : 600, lineHeight: '32px', pl: 2 + pLevel * 2 }}>
        {pGroup.label}
      </ListSubheader>,
      ...pvRenderOptions(pGroup.options || [], pLevel + 1),
      ...pvRenderGroups(pGroup.groups || [], pLevel + 1)
    ]);
  }

  return (
    <Stack alignItems="stretch">
      <Button
        id={xButtonId}
        variant="outlined"
        aria-label={ariaLabel}
        aria-haspopup="true"
        color="primary"
        aria-expanded={anchorEl ? 'true' : undefined}
        startIcon={<Icon iconName="account_activity" size={16} />}
        size="small"
        sx={{ px: 2 }}
        onClick={pEvent => setAnchorEl(pEvent.currentTarget)}
        fullWidth
        noWrap>
        <Typography variant="body2" sx={{ textTransform: 'none', whiteSpace: 'normal' }}>
          {`Ordenar por: ${xSelectedLabel}`}
        </Typography>
      </Button>

      <Menu
        id={`${id}-menu`}
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        dense
        MenuListProps={{ 'aria-labelledby': xButtonId }}>
        {groups?.length ? pvRenderGroups(groups) : pvRenderOptions(options)}
      </Menu>
    </Stack>
  );
});

SortButton.propTypes = {
  id: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string.isRequired,
      selectedLabel: PropTypes.string,
      value: PropTypes.string.isRequired
    })
  ).isRequired,
  groups: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.string.isRequired,
      options: PropTypes.array,
      groups: PropTypes.array
    })
  ),
  onChange: PropTypes.func.isRequired,
  ariaLabel: PropTypes.string
};

SortButton.displayName = 'SortButton';

export default SortButton;
